import type { ChatResponse, BrandRole, TeamMember, Invitation, UserSearchResult, InviteDetails } from './types'
import type {
  AccountRole, AccountMember, AccountInvitation, AgencyInviteDetails, MemberScopeInput,
  AgencyBrand, AgencyBrandRosterEntry, BrandTeam,
} from './agency'
import type { WalletSummary } from './wallet'
import { supabase } from './supabase'
import { WORKER_URL } from './env'
import { notifyNavBadgesStale } from './navBadges'
import { invalidateBrandCampaigns } from './brandCampaigns'


class ApiError extends Error {
  status: number
  body: string
  constructor(status: number, body: string) {
    super(body || `Request failed: ${status}`)
    this.status = status
    this.body = body
  }
}

class UnauthenticatedError extends Error {
  constructor() { super('Not authenticated') }
}

// Authenticated fetch against the worker. Pulls the current Supabase session
// and attaches Authorization: Bearer <jwt>. Callers never need to handle the
// token themselves.
async function workerFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new UnauthenticatedError()

  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  return fetch(`${WORKER_URL}${path}`, { ...init, headers })
}

async function workerJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await workerFetch(path, init)
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new ApiError(res.status, body)
  }
  return res.json() as Promise<T>
}

export { ApiError, UnauthenticatedError }

/**
 * The human sentence out of a worker error rather than the raw JSON envelope.
 *
 * ApiError.message is the response BODY verbatim, so surfacing it directly shows
 * the user `{"error":"...","code":"last_card"}`. Unwrap it and fall back to the
 * caller's own wording when the response wasn't JSON at all (a proxy 502, say).
 *
 * Two envelope shapes exist in the worker and `message` has to win:
 *   { error: 'Some human sentence.' }              — older endpoints
 *   { error: 'machine_code', message: 'Sentence' } — the team/agency endpoints
 * Reading `error` first showed the newer ones as `duplicate_brand_name`, so the
 * sentence the worker took the trouble to write never reached anyone.
 */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body) as { error?: string; message?: string }
      if (parsed?.message) return parsed.message
      if (parsed?.error) return parsed.error
    } catch { /* not JSON — use the fallback rather than dumping HTML */ }
    return fallback
  }
  return e instanceof Error && e.message ? e.message : fallback
}

/**
 * The machine code out of a worker error envelope — { error: 'machine_code',
 * message: 'Sentence' } — for callers that must BRANCH on which failure it was,
 * not just print it. Null when the body wasn't JSON of that shape, so a caller
 * can treat "unknown failure" and "some other failure" the same way.
 */
export function apiErrorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null
  try {
    const parsed = JSON.parse(e.body) as { error?: string }
    return parsed?.error ?? null
  } catch { return null }
}

// ── Chat ────────────────────────────────────────────────────────────────────

interface ChatRequest {
  message: string
  brand_id: string
  conversation_id: string | null
  history: { role: string; content: string }[]
}

export function sendChatMessage(req: ChatRequest): Promise<ChatResponse> {
  return workerJson<ChatResponse>('/api/chat', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

// ── Single-video adaptation ─────────────────────────────────────────────────

export function adaptVideo(brandId: string, videoId: string, feedbackId: string) {
  return workerJson<unknown>('/api/adapt', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, video_id: videoId, feedback_id: feedbackId }),
  })
}

// ── Constraints (learned preferences) ───────────────────────────────────────

export function updateConstraints(brandId: string) {
  return workerJson<unknown>('/api/constraints/update', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId }),
  })
}

export function listConstraints(brandId: string) {
  return workerJson<{ constraints: string[]; last_run_at: string | null; feedback_count: number }>(
    `/api/constraints/list?brand_id=${encodeURIComponent(brandId)}`
  )
}

export function removeConstraint(brandId: string, phrase: string) {
  return workerJson<{ ok: boolean; constraints: string[]; removed: boolean }>('/api/constraints/remove', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, phrase }),
  })
}

// ── Billing (Whop hosted portal) ─────────────────────────────────────────────

// Returns the Whop-hosted billing portal URL where the brand can change/add/
// remove their card, view invoices, and cancel. `url` is null when the brand
// has no active subscription (free tier). Card data never touches our app.
export function getBillingPortalUrl(brandId: string): Promise<{ url: string | null; subscribed?: boolean }> {
  return workerJson<{ url: string | null; subscribed?: boolean }>(
    `/api/whop/billing-portal?brand_id=${encodeURIComponent(brandId)}`
  )
}

// Starts a Whop setup-checkout where the brand saves a card for off-session
// reuse (the reliable way to make a card chargeable for hires — the billing
// portal's add/remove card does not guarantee this). Returns the embedded
// checkout params (session_id + email + environment) so the caller can render
// it in-app with a prefilled+locked email; `url` is the hosted-page fallback.
export function getUpdatePaymentMethodUrl(brandId: string): Promise<{
  url: string
  session_id?: string
  email?: string | null
  environment?: 'production' | 'sandbox'
}> {
  return workerJson<{
    url: string
    session_id?: string
    email?: string | null
    environment?: 'production' | 'sandbox'
  }>(
    `/api/whop/update-payment-method?brand_id=${encodeURIComponent(brandId)}`
  )
}

// A saved card as Whop reports it. We never persist these fields — they are
// fetched live from Whop each time we need to display the card on file.
export interface WhopPaymentMethod {
  id: string
  card_brand?: string
  card_last4?: string
  card_exp_month?: number
  card_exp_year?: number
  is_default?: boolean
  /** Brand-chosen nickname ("Marketing Amex"), or null if never named. Joined
   *  server-side from brand_payment_methods.label — Whop's payment-method object
   *  has no metadata field and no update endpoint, so it cannot hold this. */
  label?: string | null
}

// Lists the brand's saved cards straight from Whop (nothing is read from our
// DB — only the whop_member_id reference is used server-side to query Whop).
// Returns an empty array for brands that haven't saved a card yet.
export function listPaymentMethods(brandId: string): Promise<{
  methods: WhopPaymentMethod[]
  /** The card actually charged for hires (whop_payment_method_id, or the
   *  resolved default/first) — display THIS so the shown card matches the
   *  charged one. Null when no card is on file. */
  selected_payment_method_id?: string | null
  error?: string
}> {
  return workerJson<{
    methods: WhopPaymentMethod[]
    selected_payment_method_id?: string | null
    error?: string
  }>(
    `/api/whop/payment-methods?brand_id=${encodeURIComponent(brandId)}`
  )
}

// The card a hire will be charged to, BY NAME — the only billing fact a non-admin
// needs. Readable by admins and editors (viewers are refused), unlike
// listPaymentMethods which is the admin-only billing surface. Returns no card id,
// no last4 field, no expiry and no list: just whether a card exists and what to
// call it. `display_name` is composed server-side and already matches
// lib/cardName.cardDisplayName — the nickname when set, brand + last4 otherwise.
export function getChargedCard(brandId: string): Promise<{
  has_card: boolean
  display_name: string | null
}> {
  return workerJson<{ has_card: boolean; display_name: string | null }>(
    `/api/whop/charged-card?brand_id=${encodeURIComponent(brandId)}`
  )
}

// Switches which already-saved card is charged for creator hires (sets
// brand_configs.whop_payment_method_id server-side). Lets a brand pick an
// existing card instead of adding a new one. Does not affect the monthly
// subscription card, which Whop bills against the membership.
export function selectPaymentMethod(brandId: string, paymentMethodId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>('/api/whop/select-payment-method', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, payment_method_id: paymentMethodId }),
  })
}

// Names (or renames) a saved card. The card itself is untouched — Whop has no
// update endpoint for payment methods, so only the nickname is editable.
export function setPaymentMethodLabel(
  brandId: string, paymentMethodId: string, label: string
): Promise<{ ok: boolean; label: string }> {
  return workerJson<{ ok: boolean; label: string }>('/api/whop/payment-methods/label', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, payment_method_id: paymentMethodId, label }),
  })
}

// Removes a saved card from Whop. Refused with code 'last_card' when it's the
// brand's only one, and 'whop_refused' when Whop itself declines — most often
// because the card is the one an active subscription bills to.
export function deletePaymentMethod(
  brandId: string, paymentMethodId: string
): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(
    `/api/whop/payment-methods?brand_id=${encodeURIComponent(brandId)}` +
      `&payment_method_id=${encodeURIComponent(paymentMethodId)}`,
    { method: 'DELETE' }
  )
}

// ── Onboarding ──────────────────────────────────────────────────────────────

export interface OnboardPayload {
  brand_name: string
  category: string
  sub_categories?: string[] | null
  brand_type: string
  products?: string[]
  website_url?: string | null
  product_description?: string
  target_audience?: string | null
  product_keywords?: string[]
  delivery_email?: string
  contact_email?: string
  brief_cadence?: string
  onboarding_completed?: boolean
  // When set, creates this brand UNDER an existing agency account (the
  // "Add a brand" flow). The worker verifies the caller owns/admins the account
  // and that it's type='agency'; a solo brand account can't add a second brand.
  account_id?: string
}

// ── Wallet ──────────────────────────────────────────────────────────────────

/**
 * GET /api/hiring/wallet
 *
 * Authoritative wallet read: settled balance, credit reserved by in-flight hire
 * charges, what's spendable, and the ledger. Pass amountCents to also get the
 * credit/card split a hire of that size would produce — computed server-side by
 * the same function the charge path uses, so a preview can't promise a split the
 * charge won't honour.
 */
export function getWallet(brandId: string, amountCents?: number): Promise<WalletSummary> {
  const p = new URLSearchParams({ brand_id: brandId })
  if (amountCents && amountCents > 0) p.set('amount_cents', String(Math.round(amountCents)))
  return workerJson<WalletSummary>(`/api/hiring/wallet?${p.toString()}`)
}

// ── Wallet top-up (brand buys credit through Whop) ──────────────────────────

export interface CreditTopupTier {
  whop_plan_id: string
  amount_cents: number
  label: string | null
  sort_order: number
}

export interface CreditTopupCheckout {
  topup_id: string
  amount_cents: number
  /** Whop checkout session id — carries the topup_id through to the webhook. */
  session_id: string
  plan_id: string
  email: string | null
  environment: 'production' | 'sandbox'
  /** Whop's hosted purchase URL. Null when an already-open checkout is being
   *  reused — the embed runs off session_id, which is all the modal uses. */
  url: string | null
  return_url: string
  /** True when this handed back a checkout that was already open for this brand
   *  and tier, rather than minting another one. */
  reused?: boolean
  /** True when the brand typed the amount rather than picking a listed tier. */
  is_custom_amount?: boolean
}

/** Self-service custom-amount rules, from the server. The UI hides the field
 *  when unavailable rather than offering something that fails at submit. */
export interface CreditTopupCustomRules {
  available: boolean
  min_cents: number
  max_cents: number
}

export interface CreditTopupStatus {
  topup_id: string
  /** 'processing' = authorised and in flight. For ACH that lasts 3-5 business
   *  days and no credit exists yet. */
  status: 'pending' | 'processing' | 'credited' | 'failed' | 'reversed' | 'abandoned'
  expected_amount_cents: number
  credited_cents: number | null
  error_message: string | null
  /** Rail Whop reported: 'card' | 'ach' | 'crypto' | its raw value. NULL until
   *  Whop names it — for a card that is only at settlement — so null means
   *  UNKNOWN and must never be presented as a bank transfer. */
  payment_method: string | null
  created_at: string
  credited_at: string | null
}

/**
 * GET /api/whop/credit-topup/tiers
 *
 * The sellable price points, from the DB rather than the bundle — repricing is
 * an UPDATE, and `available: false` (nobody has configured the real Whop plan
 * ids yet) is what hides the top-up entry point entirely.
 */
export function getCreditTopupTiers(brandId: string) {
  return workerJson<{
    tiers: CreditTopupTier[]
    available: boolean
    /** True when the worker's CREDIT_TOPUP_MODE kill switch is off/frozen, as
     *  opposed to `available: false` for "no tiers configured". Both hide the
     *  entry point; this only separates the two reasons for anyone debugging. */
    disabled?: boolean
    custom: CreditTopupCustomRules
  }>(`/api/whop/credit-topup/tiers?brand_id=${encodeURIComponent(brandId)}`)
}

/**
 * POST /api/whop/credit-topup/checkout
 *
 * Sends a PLAN, never an amount: the price is resolved server-side from the
 * tier table, so nothing the browser sends can change what gets credited. This
 * moves no money — it only opens a checkout session.
 */
export function createCreditTopupCheckout(brandId: string, whopPlanId: string, returnUrl?: string) {
  return workerJson<CreditTopupCheckout>('/api/whop/credit-topup/checkout', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, whop_plan_id: whopPlanId, return_url: returnUrl }),
  })
}

/**
 * POST /api/whop/credit-topup/checkout with a typed amount.
 *
 * Sends cents, and the server turns them into a credit_topup_tiers row before
 * anything is charged — so the amount a checkout is worth is still read from a
 * row, never from this call. Bounds are enforced server-side; the values here
 * are only for labelling the field.
 */
export function createCustomCreditTopupCheckout(brandId: string, amountCents: number, returnUrl?: string) {
  return workerJson<CreditTopupCheckout>('/api/whop/credit-topup/checkout', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, amount_cents: amountCents, return_url: returnUrl }),
  })
}

/**
 * GET /api/whop/credit-topup/status
 *
 * Polled after checkout: the embed reports success as soon as the card clears,
 * which is before Whop has delivered payment.succeeded to our webhook. Until
 * this leaves 'pending' the credit genuinely isn't there yet.
 */
export function getCreditTopupStatus(topupId: string) {
  return workerJson<CreditTopupStatus>(
    `/api/whop/credit-topup/status?topup_id=${encodeURIComponent(topupId)}`
  )
}

export function onboardBrand(payload: OnboardPayload) {
  return workerJson<{ brand_id: string; status: string }>('/api/onboard', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// ── Agency upgrade ($997) ─────────────────────────────────────────────────────
// Where a Whop checkout return lands. Agency returns carry ?agency=1 so
// CheckoutActivation watches the ACCOUNT (the brand row never gets an agency sub).
export const AGENCY_CHECKOUT_RETURN_URL = `${window.location.origin}/?from=checkout&agency=1`

// The worker's /api/whop/checkout response. Hosted pages are retired — the app
// renders the checkout in-app via Whop Elements (WhopCheckoutModal) using session_id.
// `url` is only returned by the staging-bypass path (kept as a fallback).
export interface CheckoutResponse {
  url?: string
  checkout_id?: string
  session_id?: string
  plan_id?: string
  email?: string | null
  environment?: 'production' | 'sandbox'
}

// Starts a Whop checkout for the $997 agency plan, scoped to the brand's account.
// On payment the membership webhook flips the account to type='agency' (and
// cancels the brand's old $497 sub). Returns the EMBEDDED checkout params — the
// caller renders WhopCheckoutModal (staging-bypass returns { url } → redirect).
// Pass brandId for a brand→agency switch (resolves the account from the brand),
// or accountId for a brandless agency activating its $997 directly.
export function createAgencyCheckout(opts: { brandId?: string; accountId?: string }): Promise<CheckoutResponse> {
  return workerJson<CheckoutResponse>('/api/whop/checkout', {
    method: 'POST',
    body: JSON.stringify({
      ...(opts.brandId ? { brand_id: opts.brandId } : {}),
      ...(opts.accountId ? { account_id: opts.accountId } : {}),
      tier: 'agency',
      mode: 'upgrade',
      return_url: AGENCY_CHECKOUT_RETURN_URL,
    }),
  })
}

// Remove (soft-archive) a brand from its agency. Owner/admin only; agency brands
// only. The brand disappears from the agency lobby/switcher; its data is kept.
export function removeBrandFromAgency(brandId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>('/api/account/remove-brand', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId }),
  })
}

// Cancel the agency's $997 subscription (at period end). Owner only. Account-level.
// Captures a cancellation reason (+ free-text) like the brand cancel flow.
export function cancelAgencySubscription(
  accountId: string,
  opts?: { reason?: string; other_text?: string },
): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>('/api/account/cancel-subscription', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, reason: opts?.reason, other_text: opts?.other_text }),
  })
}

// Update the agency's own profile (name, logo image, description) from Agency
// Settings. Owner only; agency accounts only — never touches any brand. Call
// refreshAccount() after so the sidebar/lobby pick up the changes. Omit a field
// to leave it unchanged; pass null to clear logo_url/description.
export function updateAgency(
  accountId: string,
  updates: { name: string; logo_url?: string | null; website?: string | null; description?: string | null },
): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>('/api/account/update', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, ...updates }),
  })
}

// ── Per-brand payment cards (Agency Accounts §4) ──────────────────────────────
// Each agency brand is its own payment identity. "Add a card" opens a Whop
// setup-intent checkout (returns a hosted URL the caller redirects to); the card
// is persisted by the setup_intent.succeeded webhook into brand_payment_methods.
// set-default / remove are service-role writes through the worker (admin-only).

export function addBrandCard(brandId: string, label: string): Promise<{ url: string }> {
  return workerJson<{ url: string }>('/api/brand-cards/setup', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, label }),
  })
}

export function setDefaultBrandCard(brandId: string, cardId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>('/api/brand-cards/default', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, card_id: cardId }),
  })
}

export function removeBrandCard(brandId: string, cardId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(
    `/api/brand-cards?brand_id=${encodeURIComponent(brandId)}&card_id=${encodeURIComponent(cardId)}`,
    { method: 'DELETE' }
  )
}

// ── Team / multi-user ─────────────────────────────────────────────────────
// All team management goes through the worker (service role) so it can drive
// the Supabase admin API for user creation/password resets and enforce role
// guards server-side. workerJson auto-attaches the caller's JWT.

export function listTeamMembers(brandId: string): Promise<{ members: TeamMember[] }> {
  return workerJson<{ members: TeamMember[] }>(
    `/api/team/members?brand_id=${encodeURIComponent(brandId)}`
  )
}

// ── Invitations (invite-by-email) ──────────────────────────────────────────

export function listInvitations(brandId: string): Promise<{ invitations: Invitation[] }> {
  return workerJson<{ invitations: Invitation[] }>(
    `/api/team/invitations?brand_id=${encodeURIComponent(brandId)}`
  )
}

// Invite someone by email. The worker creates a pending invite + emails a
// tokenized accept link. No password — the invitee sets their own on accept.
export function inviteTeamMember(
  brandId: string,
  input: { email: string; role: BrandRole }
): Promise<Invitation & { is_new_user: boolean }> {
  return workerJson<Invitation & { is_new_user: boolean }>('/api/team/invitations', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, email: input.email, role: input.role }),
  })
}

export function resendInvitation(brandId: string, inviteId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(`/api/team/invitations/${encodeURIComponent(inviteId)}/resend`, {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId }),
  })
}

export function revokeInvitation(brandId: string, inviteId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(
    `/api/team/invitations/${encodeURIComponent(inviteId)}?brand_id=${encodeURIComponent(brandId)}`,
    { method: 'DELETE' }
  )
}

// Live search of existing users by email (min 3 chars; admin-only server-side).
export function searchUsers(brandId: string, q: string): Promise<{ results: UserSearchResult[] }> {
  return workerJson<{ results: UserSearchResult[] }>(
    `/api/team/search?brand_id=${encodeURIComponent(brandId)}&q=${encodeURIComponent(q)}`
  )
}

// ── Public invite accept (no auth — the token is the credential) ────────────

export function getInvite(token: string): Promise<InviteDetails> {
  return fetch(`${WORKER_URL}/api/team/invite/${encodeURIComponent(token)}`)
    .then(async res => {
      const body = await res.text().catch(() => '')
      if (!res.ok) throw new ApiError(res.status, body)
      return JSON.parse(body) as InviteDetails
    })
}

export interface AcceptInviteResult {
  ok: boolean
  email: string
  brand_id?: string
  /** Single-use magic-link token — exchange via supabase.auth.verifyOtp to sign
   * the invitee in automatically (no separate login step). */
  login_token_hash?: string | null
}

export function acceptInvite(token: string, password?: string): Promise<AcceptInviteResult> {
  return fetch(`${WORKER_URL}/api/team/invite/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(password ? { password } : {}),
  }).then(async res => {
    const body = await res.text().catch(() => '')
    if (!res.ok) throw new ApiError(res.status, body)
    return JSON.parse(body) as AcceptInviteResult
  })
}

export function updateTeamMember(
  brandId: string,
  userId: string,
  updates: { role?: BrandRole }
): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(`/api/team/members/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ brand_id: brandId, ...updates }),
  })
}

/** Removing an agency-OVERRIDDEN member doesn't remove them: it withdraws this
 *  brand's own underlying grant and leaves the agency's access in place. The
 *  worker says which happened via `withdrawn` + a ready-to-show `message`. */
export function removeTeamMember(
  brandId: string, userId: string
): Promise<{ ok: boolean; withdrawn?: 'native_grant'; message?: string }> {
  return workerJson<{ ok: boolean; withdrawn?: 'native_grant'; message?: string }>(
    `/api/team/members/${encodeURIComponent(userId)}?brand_id=${encodeURIComponent(brandId)}`,
    { method: 'DELETE' }
  )
}

// ── Agency team (account-level members + per-brand scoping) ────────────────
// The account-level sibling of the brand team calls above. Mutations are gated
// on account_members owner/admin server-side; the accept pair is public (the
// token in the emailed link is the credential).

// Also returns the account's FULL brand roster (service-role read), which the
// assignment picker needs: useAccount().brands is scoped to the caller's own
// access, so it can't be used to offer brands an agency admin isn't in.
/** The agency's FULL brand roster, each flagged with whether the CALLER can open
 *  it. Owner/admin only. useAccount().brands is intersected with the caller's own
 *  access, so an admin scoped to a subset can't see the rest — the lobby uses this
 *  to show them every brand, with the unassigned ones inert. */
export function listAgencyBrands(
  accountId: string
): Promise<{ brands: AgencyBrandRosterEntry[] }> {
  return workerJson<{ brands: AgencyBrandRosterEntry[] }>(
    `/api/agency/brands?account_id=${encodeURIComponent(accountId)}`
  )
}

export function listAccountMembers(
  accountId: string
): Promise<{ members: AccountMember[]; brands: AgencyBrand[]; brand_teams?: BrandTeam[] }> {
  return workerJson<{ members: AccountMember[]; brands: AgencyBrand[]; brand_teams?: BrandTeam[] }>(
    `/api/agency/team/members?account_id=${encodeURIComponent(accountId)}`
  )
}

export function listAccountInvitations(accountId: string): Promise<{ invitations: AccountInvitation[] }> {
  return workerJson<{ invitations: AccountInvitation[] }>(
    `/api/agency/team/invitations?account_id=${encodeURIComponent(accountId)}`
  )
}

// Invite someone to the agency. `scope` decides which brands they land in — the
// worker validates every brand_id belongs to this account.
export function inviteAccountMember(
  accountId: string,
  input: { email: string; role: AccountRole } & MemberScopeInput
): Promise<AccountInvitation & { is_new_user: boolean }> {
  return workerJson<AccountInvitation & { is_new_user: boolean }>('/api/agency/team/invitations', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, ...input }),
  })
}

export function resendAccountInvitation(accountId: string, inviteId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(
    `/api/agency/team/invitations/${encodeURIComponent(inviteId)}/resend`,
    { method: 'POST', body: JSON.stringify({ account_id: accountId }) }
  )
}

export function revokeAccountInvitation(accountId: string, inviteId: string): Promise<{ ok: boolean }> {
  return workerJson<{ ok: boolean }>(
    `/api/agency/team/invitations/${encodeURIComponent(inviteId)}?account_id=${encodeURIComponent(accountId)}`,
    { method: 'DELETE' }
  )
}

export function searchAccountUsers(accountId: string, q: string): Promise<{ results: UserSearchResult[] }> {
  return workerJson<{ results: UserSearchResult[] }>(
    `/api/agency/team/search?account_id=${encodeURIComponent(accountId)}&q=${encodeURIComponent(q)}`
  )
}

/** What a scope/role change actually did, per brand — the page uses it to say
 *  "granted 2, revoked 1" rather than a bare "Saved". */
export interface ReconcileResult {
  ok: boolean
  granted: { brand_id: string; role: BrandRole }[]
  /** `claimed` marks a role the agency took over from the brand's own Team page. */
  updated: { brand_id: string; role: BrandRole; from: string; claimed?: boolean }[]
  /** Taken over from a brand's own team at a role that already matched: the
   *  person's access is unchanged, but the agency now controls the row. */
  claimed: { brand_id: string; role: BrandRole }[]
  /** Access deleted — grants the agency had created itself. */
  revoked: string[]
  /** Overrides lifted: the person KEEPS these brands, back at the role the brand
   *  itself granted. Never report these as access lost. */
  restored: { brand_id: string; role: BrandRole }[]
}

export function updateAccountMember(
  accountId: string,
  userId: string,
  updates: { role?: AccountRole } & MemberScopeInput
): Promise<ReconcileResult> {
  return workerJson<ReconcileResult>(`/api/agency/team/members/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ account_id: accountId, ...updates }),
  })
}

export function removeAccountMember(
  accountId: string, userId: string
): Promise<{ ok: boolean; revoked: string[]; restored: { brand_id: string; role: BrandRole }[] }> {
  return workerJson<{ ok: boolean; revoked: string[]; restored: { brand_id: string; role: BrandRole }[] }>(
    `/api/agency/team/members/${encodeURIComponent(userId)}?account_id=${encodeURIComponent(accountId)}`,
    { method: 'DELETE' }
  )
}

// ── Public agency invite accept (no auth — the token is the credential) ──────

export function getAgencyInvite(token: string): Promise<AgencyInviteDetails> {
  return fetch(`${WORKER_URL}/api/agency/team/invite/${encodeURIComponent(token)}`)
    .then(async res => {
      const body = await res.text().catch(() => '')
      if (!res.ok) throw new ApiError(res.status, body)
      return JSON.parse(body) as AgencyInviteDetails
    })
}

export interface AcceptAgencyInviteResult {
  ok: boolean
  email: string
  account_id?: string
  /** First brand they were granted, so the page can land them inside it rather
   *  than at an empty lobby. Null when the agency has no brands yet. */
  brand_id?: string | null
  login_token_hash?: string | null
}

export function acceptAgencyInvite(token: string, password?: string): Promise<AcceptAgencyInviteResult> {
  return fetch(`${WORKER_URL}/api/agency/team/invite/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(password ? { password } : {}),
  }).then(async res => {
    const body = await res.text().catch(() => '')
    if (!res.ok) throw new ApiError(res.status, body)
    return JSON.parse(body) as AcceptAgencyInviteResult
  })
}



// ── Campaigns ───────────────────────────────────────────────────────────────

/** Copies a campaign into a new draft and returns it. The copy carries the
 *  brief, product and targeting but none of the run — no spend, hires, videos
 *  or launch emails. Navigate to /campaigns/new?draft=<id> to edit it. */
export async function duplicateCampaign(campaignId: string): Promise<{ campaign: { id: string; name: string } }> {
  const res = await workerJson<{ campaign: { id: string; name: string } }>(
    `/api/hiring/campaigns/${encodeURIComponent(campaignId)}/duplicate`,
    { method: 'POST' },
  )
  invalidateBrandCampaigns()   // there is a new campaign
  return res
}

export interface CampaignAvailability {
  /** Present only when product_id was asked about. Unavailable ALWAYS means a
   *  DIFFERENT brand holds it (scope 'other'), and no campaign name comes back
   *  — names aren't disclosed across tenants. The brand's own live campaigns
   *  on the same product are allowed and report available. */
  product?: { available: boolean; scope?: 'other' }
  /** Present only when bundle_product_id was asked about. Same verdict shape:
   *  a bundle listing is an ordinary TikTok Shop product, checked against
   *  every live product AND bundle id rather than a namespace of its own —
   *  and, like the product field, only another brand's claim blocks it. */
  bundle?: { available: boolean; scope?: 'other' }
  /** Present only when name was asked about. Always within the caller's brand. */
  name?: { available: boolean; campaign_name?: string; status?: string }
}

/** Pre-flight for the campaign wizard: is this product ID free, and is this
 *  campaign name unused? Runs the same guards the launch path runs.
 *
 *  Goes through the worker rather than querying cr_campaigns from the browser:
 *  the creator-facing "read every active campaign" RLS policy applies to brand
 *  users too, so a direct query would surface other brands' campaign names. */
export function checkCampaignAvailability(
  brandId: string,
  opts: { productId?: string; bundleProductId?: string; name?: string; excludeCampaignId?: string }
): Promise<CampaignAvailability> {
  return workerJson('/api/hiring/campaigns/check-availability', {
    method: 'POST',
    body: JSON.stringify({
      brand_id: brandId,
      product_id: opts.productId,
      bundle_product_id: opts.bundleProductId,
      name: opts.name,
      exclude_campaign_id: opts.excludeCampaignId,
    }),
  })
}

// ── ROI & Analytics ────────────────────────────────────────────────────────

export type RoiRange = '7d' | '30d' | '90d' | 'all'

export interface RoiChartBucket {
  label: string
  cum_cents: number
  projected: boolean
}

export interface RoiDelta {
  pct: number
  up: boolean
}

/**
 * How the worker derived the window figures. These are NOT interchangeable and
 * the page must word itself differently for each — see worker/src/analytics.ts.
 *
 *   'period'   — true earned-in-window deltas from daily snapshots. What a brand
 *                means by "GMV in the last 30 days".
 *   'cohort'   — fallback while snapshot history is shorter than the window:
 *                content POSTED in the window, counted at its lifetime totals.
 *                Must never be labelled as earnings-in-window, and carries no
 *                period-over-period deltas (the worker returns them as null).
 *   'lifetime' — the All range, where window == lifetime, so it's exact.
 */
export type RoiBasis = 'period' | 'cohort' | 'lifetime'

/** Response of GET /api/analytics/roi. Every figure is computed in the worker
 *  (see worker/src/analytics.ts) — the page is a pure renderer. When the brand
 *  has no hires at all, `has_hires` is false and no other field is present. */
export interface RoiAnalyticsPayload {
  range: RoiRange
  /** Echoed back when a campaign filter was applied; null for brand-wide. */
  campaign_id?: string | null
  has_hires: boolean
  generated_at: string
  updated_at?: string | null
  /** null on the "all" range, which has no comparison window. */
  window_days?: number | null

  basis?: RoiBasis
  /** First day of snapshot history for this brand (ISO date), or null if the
   *  enrich cron hasn't recorded any yet. Explains why a window is cohort-based. */
  coverage_since?: string | null

  totals?: { gmv_cents: number; views: number; engagement: number; orders: number }
  deltas?: { gmv: RoiDelta | null; views: RoiDelta | null; engagement: RoiDelta | null }

  hero?: {
    active_posting: number
    hired_count: number
    creators_with_sales: number
    /** Change in creators-with-sales vs. the previous window; null on "all". */
    sales_diff: number | null
    delivered_all_time: number
    committed_all_time: number
  }

  momentum?: {
    cost_per_video_cents: number | null
    aov_cents: number | null
    new_hires: number
  }

  chart?: {
    buckets: RoiChartBucket[]
    actual_count: number
    bucket_noun: 'month' | 'week' | 'day'
  }
  lifetime_gmv_cents?: number

  top_videos?: {
    id: string
    rank: number
    creator_handle: string
    views: number
    gmv_cents: number
    thumbnail_url: string | null
    tiktok_video_url: string | null
    video_storage_url: string | null
  }[]
  leaderboard?: { creator_id: string; creator_handle: string; gmv_cents: number }[]
  leaderboard_rest?: { count: number; gmv_cents: number } | null
  repeat_creators?: {
    creator_id: string
    creator_handle: string
    initials: string
    avatar_url: string | null
    campaigns: number
  }[]
  pipeline?: { hire_id: string; creator_handle: string; stage: string }[]
  pipeline_overflow?: number
  watch_list?: {
    creator_id: string
    creator_handle: string
    views: number
    videos: number
    signal: string
  }[]
  watch_views_threshold?: number
  projection_decay_pct?: number
}

export function getRoiAnalytics(brandId: string, range: RoiRange, campaignId?: string): Promise<RoiAnalyticsPayload> {
  const campaignParam = campaignId ? `&campaign_id=${encodeURIComponent(campaignId)}` : ''
  return workerJson<RoiAnalyticsPayload>(
    `/api/analytics/roi?brand_id=${encodeURIComponent(brandId)}&range=${encodeURIComponent(range)}${campaignParam}`
  )
}

// ── Audit log ──────────────────────────────────────────────────────────────

export interface AuditLogInput {
  brand_id: string
  action: string
  entity_type?: string
  entity_id?: string
  summary?: string
  metadata?: Record<string, unknown>
}

// Fire-and-forget: record an audit entry for a direct-from-frontend write. The
// worker derives the actor (user / role / email) server-side from the JWT, so
// the client can't forge "who did it". Never throws — logging must never break
// the action it's recording.
export function logAudit(input: AuditLogInput): void {
  void workerFetch('/api/audit/log', {
    method: 'POST',
    body: JSON.stringify(input),
  }).catch(() => { /* best-effort */ })
}

export interface AuditLogRow {
  id: string
  actor_user_id: string | null
  actor_email: string | null
  actor_role: string | null
  action: string
  entity_type: string | null
  entity_id: string | null
  summary: string | null
  metadata: Record<string, unknown> | null
  created_at: string
  /** Credit-purchase rows only: the top-up's CURRENT status, attached by the
   *  worker at read time (the log row itself is frozen at write time). */
  topup?: {
    status: CreditTopupStatus['status']
    payment_method: string | null
    created_at: string
  } | null
}

/** Both Activity listings hide actions a BMC admin performed via "switch as user"
 *  (the full record lives in the admin dashboard). `hidden_impersonated` is how
 *  many rows that removed, so an empty-looking log can explain itself instead of
 *  reading as "nothing is being recorded". */
export interface AuditLogPage {
  logs: AuditLogRow[]
  hidden_impersonated?: number
}

// Admin-only. Returns brand audit log rows, newest first.
export function getAuditLogs(
  brandId: string,
  opts: { limit?: number; offset?: number; action?: string } = {}
): Promise<AuditLogPage> {
  const params = new URLSearchParams({ brand_id: brandId })
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  if (opts.action) params.set('action', opts.action)
  return workerJson<AuditLogPage>(`/api/audit/logs?${params.toString()}`)
}

// Agency owner/admin only. Returns ACCOUNT-level audit rows, newest first —
// agency team + roster activity, which deliberately no longer lands in any
// brand's log. Same row shape as the brand log, so the Activity page renders
// both from one component.
export function getAgencyAuditLogs(
  accountId: string,
  opts: { limit?: number; offset?: number; action?: string } = {}
): Promise<AuditLogPage> {
  const params = new URLSearchParams({ account_id: accountId })
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  if (opts.action) params.set('action', opts.action)
  return workerJson<AuditLogPage>(`/api/agency/audit/logs?${params.toString()}`)
}

// ── TikTok status / refresh (lives on the Brand-Dashboard worker) ──────────

export interface TiktokStatus {
  connected: boolean
  seller_name?: string | null
  shop_id?: string | null
  access_token_expires_at?: string
  refresh_token_expires_at?: string | null
  granted_scopes?: string[] | null
}

/** Cheap probe for the brand's TikTok connection state. Returns `connected:
 *  false` for brands that have never connected; otherwise returns the active
 *  token's expiry + seller info. */
export function getTiktokStatus(brandId: string): Promise<TiktokStatus> {
  return workerJson<TiktokStatus>(`/api/tiktok/status?brand_id=${encodeURIComponent(brandId)}`)
}

/** Manually refreshes the brand's TikTok access token. Usually unnecessary —
 *  the worker auto-refreshes inside getValidToken() before any shop request —
 *  but useful for admin tools / debugging. */
export function refreshTiktokToken(brandId: string) {
  return workerJson<{
    status: string
    access_token_expires_at: string
    refresh_token_expires_at: string | null
    seller_name: string | null
  }>('/api/tiktok/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId }),
  })
}

// ── TikTok sample applications ──────────────────────────────────────────────
// Mirror of the seller "sample applications" data, matched to this brand's
// hires by the worker (creator_open_id + product_id, scoped to the brand's own
// shop token). See worker/src/tiktok/samples.ts.

export interface TiktokSampleApplication {
  id: string
  brand_id: string
  tiktok_application_id: string
  creator_open_id: string | null
  creator_username: string | null
  creator_nickname: string | null
  product_tiktok_id: string | null
  product_title: string | null
  order_id: string | null
  matched_hire_id: string | null
  matched_campaign_id: string | null
  status: string | null
  fulfillment_status: string | null
  tracking_number: string | null
  is_approvable: boolean | null
  disapprovable_reasons: string[] | null
  commission_rate: string | null
  available_quantity: number | null
  partner_name: string | null
  approve_expiration_time: string | null
  shipment_expiration_time: string | null
  creator: {
    creator_open_id?: string
    username?: string
    nickname?: string
    follower_count?: number
    avatar_url?: string
    gmv?: { amount?: string; currency?: string }
    content_count?: number
    fulfillment_percentage?: string
    ec_video_view?: number
  } | null
  product: {
    id?: string
    title?: string
    sku_id?: string
    sku_image_url?: string
    sku_name?: string
  } | null
  first_seen_at: string
  status_changed_at: string
  last_synced_at: string
}

/** Reads the mirrored sample applications for a brand (no TikTok call). Call
 *  syncTiktokSamples first to pull the latest from TikTok. */
export function getTiktokSamples(
  brandId: string,
  opts: { matchedOnly?: boolean; status?: string; limit?: number } = {}
): Promise<{ status: string; count: number; sample_applications: TiktokSampleApplication[] }> {
  const params = new URLSearchParams({ brand_id: brandId })
  if (opts.matchedOnly) params.set('matched', '1')
  if (opts.status) params.set('status', opts.status)
  if (opts.limit != null) params.set('limit', String(opts.limit))
  return workerJson(`/api/tiktok/samples?${params.toString()}`)
}

/**
 * How many sample applications match — for the sidebar badge, which needs a
 * number and nothing else.
 *
 * Sends count_only=1. A worker that understands it answers with a PostgREST
 * count and an empty body (one cheap query, no rows over the wire). An OLDER
 * worker ignores the param and returns rows as before, so we detect that by the
 * absent `count_only` marker and count them here exactly as the sidebar used
 * to — which is what lets the frontend and worker deploy in either order.
 */
export async function getTiktokSampleCount(
  brandId: string,
  status: string,
): Promise<number> {
  const params = new URLSearchParams({ brand_id: brandId, count_only: '1', status })
  const res = await workerJson<{
    count?: number
    count_only?: boolean
    sample_applications?: TiktokSampleApplication[]
  }>(`/api/tiktok/samples?${params.toString()}`)

  if (res.count_only) return res.count ?? 0

  // Legacy worker: it filtered `status` case-SENSITIVELY, so re-filter here the
  // way the sidebar always did rather than trusting the server-side match.
  const want = status.toUpperCase()
  return (res.sample_applications ?? []).filter(r => (r.status || '').toUpperCase() === want).length
}

/** Triggers a live pull from TikTok (one search per campaign product) → match
 *  to hires → upsert, then returns the sync summary. Only requests from hired
 *  creators are stored. The listing should be re-fetched afterward. */
export function syncTiktokSamples(
  brandId: string
): Promise<{
  status: string
  brandId: string
  products: number
  scanned: number
  stored: number
  productErrors: Array<{ productId: string; error: string }>
}> {
  return workerJson('/api/tiktok/samples/sync', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId }),
  })
}

export type SampleReviewResult = 'APPROVE' | 'REJECT'
export type SampleRejectReason = 'NOT_MATCH' | 'OFFLINE' | 'OUT_OF_STOCK' | 'OTHER'

/** Approve or reject a sample application on TikTok, on behalf of the brand.
 *  reject_reason is required when reviewResult is 'REJECT'. Admin/editor only. */
export async function reviewTiktokSample(
  brandId: string,
  applicationId: string,
  reviewResult: SampleReviewResult,
  rejectReason?: SampleRejectReason
): Promise<{ status: string; applicationId: string; reviewResult: SampleReviewResult; newStatus: string }> {
  const res = await workerJson<{ status: string; applicationId: string; reviewResult: SampleReviewResult; newStatus: string }>(
    '/api/tiktok/samples/review',
    {
      method: 'POST',
      body: JSON.stringify({
        brand_id: brandId,
        application_id: applicationId,
        review_result: reviewResult,
        reject_reason: rejectReason,
      }),
    },
  )
  // One fewer pending sample. Reviewed through the worker, so nothing
  // client-side would otherwise tell the nav badge.
  notifyNavBadgesStale()
  return res
}

// ── TikTok Shop OAuth ────────────────────────────────────────────────────────

// Asks the worker for a TikTok Shop authorize URL scoped to this brand. The
// worker persists a state row in tiktok_auth_states, then we redirect the
// browser to TikTok. After the user authorizes, TikTok hits the registered
// redirect_uri (Express /api callback), which forwards to the worker's
// /api/tiktok/callback to finish the token exchange + Supabase writes.
export async function getTiktokBrandAuthUrl(brandId: string): Promise<string> {
  const data = await workerJson<{ authorizeUrl?: string }>(
    `/api/tiktok/auth/brand?brand_id=${encodeURIComponent(brandId)}`
  )
  if (!data.authorizeUrl) throw new Error('No authorize URL returned')
  return data.authorizeUrl
}

// Forces a real save-to-disk of the delivered video. The R2 object itself is
// stored Content-Disposition: inline (so the same underlying video plays
// directly in the "watch" popup / a direct navigation) — fetching it straight
// from the browser also hits CORS, since R2's public r2.dev domain sends no
// Access-Control-Allow-Origin header. video-download-file sidesteps both: it
// streams the video through the worker (which already sends proper CORS
// headers on every route) and forces attachment disposition on its own
// response, independent of the stored object. The worker mirrors from TikTok
// into R2 on first call (a few seconds), then serves the cached copy.
export async function downloadHiredVideo(submissionId: string): Promise<void> {
  const res = await workerFetch(`/api/hiring/video-download-file?submission_id=${encodeURIComponent(submissionId)}`)
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new ApiError(res.status, body)
  }
  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)
  const disposition = res.headers.get('Content-Disposition') || ''
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] || 'video.mp4'
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking immediately can cancel the download in some browsers (Safari/
  // Firefox) before it's actually picked it up — give it a beat.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000)
}
